import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Switch,
  Box,
  Typography,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Skeleton,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  Send as SendIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { NotificationTemplate } from '../../../types/notification-admin';
import { NotificationType, NotificationPriority } from '../../../types/user';

interface TemplateListProps {
  templates: NotificationTemplate[];
  loading?: boolean;
  onEdit: (template: NotificationTemplate) => void;
  onDelete: (templateId: string) => void;
  onToggleStatus: (templateId: string, enabled: boolean) => void;
  onViewHistory: (templateId: string) => void;
  onTestSend: (template: NotificationTemplate) => void;
  onPreview: (template: NotificationTemplate) => void;
}

/**
 * TemplateList Component
 * Displays a searchable, filterable table of notification templates
 */
export const TemplateList: React.FC<TemplateListProps> = ({
  templates,
  loading = false,
  onEdit,
  onDelete,
  onToggleStatus,
  onViewHistory,
  onTestSend,
  onPreview,
}) => {
  const [searchText, setSearchText] = useState('');
  const [filterPriority, setFilterPriority] = useState<NotificationPriority | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'enabled' | 'disabled'>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');

  /**
   * Get category from notification type
   */
  const getCategory = (type: NotificationType): string => {
    const typeStr = type.toString();
    if (typeStr.startsWith('ACCOUNT_') || typeStr.startsWith('PASSWORD_') || typeStr.startsWith('ROLE_')) {
      return 'Authentication';
    }
    if (typeStr.startsWith('CONTENT_') || typeStr.startsWith('EDIT_')) {
      return 'Content Moderation';
    }
    if (typeStr.includes('COMMENT') || typeStr.includes('TAGGED') || typeStr.includes('RELATIONSHIP_SUGGESTED')) {
      return 'Social Interactions';
    }
    if (typeStr.startsWith('PERSON_') || typeStr.startsWith('RELATIONSHIP_') || typeStr.startsWith('PHOTO_') || typeStr.startsWith('MEMORY_')) {
      return 'Family Tree Updates';
    }
    if (typeStr.startsWith('RESEARCH_') || typeStr.startsWith('DOCUMENT_') || typeStr.startsWith('COLLABORATION_')) {
      return 'Research & Collaboration';
    }
    if (typeStr.includes('REMINDER') || typeStr.includes('PROFILE_INCOMPLETE')) {
      return 'Reminders';
    }
    return 'System & Admin';
  };

  /**
   * Format notification type for display
   */
  const formatType = (type: NotificationType): string => {
    return type
      .toString()
      .split('_')
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  /**
   * Get priority color
   */
  const getPriorityColor = (
    priority: NotificationPriority
  ): 'error' | 'warning' | 'info' | 'default' => {
    switch (priority) {
      case NotificationPriority.CRITICAL:
        return 'error';
      case NotificationPriority.HIGH:
        return 'warning';
      case NotificationPriority.NORMAL:
        return 'info';
      case NotificationPriority.LOW:
        return 'default';
      default:
        return 'default';
    }
  };

  /**
   * Filter templates based on search and filters
   */
  const filteredTemplates = templates.filter((template) => {
    // Search filter
    if (searchText) {
      const search = searchText.toLowerCase();
      const typeMatch = formatType(template.type).toLowerCase().includes(search);
      const titleMatch = template.title.toLowerCase().includes(search);
      const messageMatch = template.message.toLowerCase().includes(search);
      if (!typeMatch && !titleMatch && !messageMatch) {
        return false;
      }
    }

    // Priority filter
    if (filterPriority !== 'all' && template.priority !== filterPriority) {
      return false;
    }

    // Status filter
    if (filterStatus === 'enabled' && !template.enabled) {
      return false;
    }
    if (filterStatus === 'disabled' && template.enabled) {
      return false;
    }

    // Category filter
    if (filterCategory !== 'all' && getCategory(template.type) !== filterCategory) {
      return false;
    }

    return true;
  });

  /**
   * Get unique categories from templates
   */
  const categories = Array.from(new Set(templates.map((t) => getCategory(t.type)))).sort();

  /**
   * Render loading state
   */
  if (loading) {
    return (
      <Box>
        {[...Array(5)].map((_, index) => (
          <Skeleton key={index} variant="rectangular" height={60} sx={{ mb: 1 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box>
      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search templates..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          size="small"
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={filterCategory}
            label="Category"
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <MenuItem value="all">All Categories</MenuItem>
            {categories.map((category) => (
              <MenuItem key={category} value={category}>
                {category}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Priority</InputLabel>
          <Select
            value={filterPriority}
            label="Priority"
            onChange={(e) => setFilterPriority(e.target.value as any)}
          >
            <MenuItem value="all">All Priorities</MenuItem>
            <MenuItem value={NotificationPriority.CRITICAL}>Critical</MenuItem>
            <MenuItem value={NotificationPriority.HIGH}>High</MenuItem>
            <MenuItem value={NotificationPriority.NORMAL}>Normal</MenuItem>
            <MenuItem value={NotificationPriority.LOW}>Low</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filterStatus}
            label="Status"
            onChange={(e) => setFilterStatus(e.target.value as any)}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="enabled">Enabled</MenuItem>
            <MenuItem value="disabled">Disabled</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ flexGrow: 1 }} />

        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
          {filteredTemplates.length} of {templates.length} templates
        </Typography>
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Message Preview</TableCell>
              <TableCell align="center">Priority</TableCell>
              <TableCell align="center">Variables</TableCell>
              <TableCell align="center">Version</TableCell>
              <TableCell align="center">Enabled</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTemplates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No templates found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredTemplates.map((template) => (
                <TableRow key={template.id} hover>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                      {formatType(template.type)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={getCategory(template.type)} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {template.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      noWrap
                      sx={{ maxWidth: 300 }}
                    >
                      {template.message}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={template.priority}
                      size="small"
                      color={getPriorityColor(template.priority)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={template.variables.length}
                      size="small"
                      variant="outlined"
                      color="info"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="text.secondary">
                      v{template.version}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={template.enabled}
                      onChange={(e) => onToggleStatus(template.id, e.target.checked)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      <Tooltip title="Preview">
                        <IconButton size="small" onClick={() => onPreview(template)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => onEdit(template)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Test Send">
                        <IconButton size="small" onClick={() => onTestSend(template)}>
                          <SendIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Version History">
                        <IconButton size="small" onClick={() => onViewHistory(template.id)}>
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => onDelete(template.id)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
